import React, { useEffect, useState, useRef } from 'react';
import { Bell, X, Info, CheckCircle2, AlertTriangle, Megaphone } from 'lucide-react';
import { notificationsAPI } from '../lib/api';

const TYPE_META = {
  info: { icon: Info, color: '#0ea5e9' },
  success: { icon: CheckCircle2, color: '#00e676' },
  warning: { icon: AlertTriangle, color: '#eab308' },
  alert: { icon: Megaphone, color: '#ef4444' }
};

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const ref = useRef(null);

  const loadCount = async () => { try { const r = await notificationsAPI.unreadCount(); setCount(r.count || 0); } catch {} };
  const loadList = async () => { try { setItems(await notificationsAPI.list()); } catch {} };

  useEffect(() => { loadCount(); const t = setInterval(loadCount, 20000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) await loadList();
  };

  const markRead = async (n) => {
    if (!n.read) {
      try { await notificationsAPI.markRead(n.id); } catch {}
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
      loadCount();
    }
  };
  const markAll = async () => {
    try { await notificationsAPI.markAllRead(); } catch {}
    setItems(prev => prev.map(x => ({ ...x, read: true })));
    setCount(0);
  };
  const remove = async (id) => {
    try { await notificationsAPI.remove(id); } catch {}
    setItems(prev => prev.filter(x => x.id !== id));
    loadCount();
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notification-bell" className="relative w-9 h-9 rounded-lg border border-[#1a2823] flex items-center justify-center text-gray-400 hover:text-[#00e676]">
        <Bell size={15}/>
        {count > 0
          ? <span data-testid="notification-badge" className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#ef4444] text-white text-[9px] font-bold flex items-center justify-center">{count > 9 ? '9+' : count}</span>
          : <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#00e676] pulse-dot"/>}
      </button>
      {open && (
        <div data-testid="notification-panel" className="absolute right-0 mt-2 w-80 max-w-[92vw] rounded-xl border border-[#1a2823] bg-[#0a1210] shadow-2xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-[#141f1b] flex items-center justify-between">
            <span className="text-sm font-semibold text-white">Notifications</span>
            {items.some(i => !i.read) && <button onClick={markAll} data-testid="notif-mark-all" className="text-[11px] text-[#00e676] hover:underline">Mark all read</button>}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && <div className="py-10 text-center text-xs text-gray-500">No messages yet</div>}
            {items.map(n => {
              const meta = TYPE_META[n.type] || TYPE_META.info;
              const Ico = meta.icon;
              return (
                <div key={n.id} onClick={() => markRead(n)} data-testid={`notification-item-${n.id}`} className={`px-4 py-3 border-b border-[#141f1b] cursor-pointer hover:bg-[#0f1a17] ${!n.read ? 'bg-[#0e1613]' : ''}`}>
                  <div className="flex items-start gap-2.5">
                    <div className="mt-0.5 w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: `${meta.color}1a`, color: meta.color }}><Ico size={13}/></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-white truncate">{n.title}</span>
                        {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#00e676] flex-shrink-0"/>}
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5 whitespace-pre-wrap break-words">{n.message}</p>
                      <span className="text-[10px] text-gray-600 mono">{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</span>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); remove(n.id); }} className="text-gray-600 hover:text-red-400 flex-shrink-0"><X size={13}/></button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
